import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import CalendarShell from '../calendar/CalendarShell';
import { getTimeFormat } from 'common/dateFormatConfig';
import useScheduleDayEventsLoad from '../hooks/useScheduleDayEventsLoad';
import useV6CrewLeadersEventColumns from './hooks/useV6CrewLeadersEventColumns';
import { TIME_VIEW_COLUMN_WIDTH } from 'pages/ScheduleV6/constants/constants';
import EventCard from '../events/EventCard';
import useV6CrewLeadersEvents from './hooks/useV6CrewLeadersEvents';
import CrewLeaderHeader from './cl/CrewLeaderHeader';

/**
 * Crew Leader view.
 *
 * Mapping and heavy logic must live in hooks.
 */
const CrewLeaderView = ({
  calendarRef,
  selectedDate,
  dateFormat,
  timeFormat,
  noClSheets,
  hasEditAccess,
}) => {
  useScheduleDayEventsLoad(selectedDate);
  const events = useV6CrewLeadersEvents();
  const { eventsWithColumns, crewLeaders } = useV6CrewLeadersEventColumns(events);

  const timeSettings = useMemo(
    () => getTimeFormat([], timeFormat, dateFormat),
    [timeFormat, dateFormat]
  );

  const slotLabelFormat = useMemo(
    () =>
      timeSettings.is12Format
        ? undefined
        : {
            hour: '2-digit',
            minute: '2-digit',
          },
    [timeSettings.is12Format]
  );

  useEffect(() => {
    if (calendarRef?.current) {
      console.log(calendarRef?.current);
      calendarRef.current?.elRef?.current?.style?.setProperty('--crew-leaders-width', `${crewLeaders?.length * 204}px`)
    }
  }, [calendarRef, crewLeaders]);

  useEffect(() => {
    const maxColumn = Math.max(...eventsWithColumns.map(event => event.extendedProps?.column));

    if (calendarRef?.current?.elRef) {
      calendarRef.current.elRef.current.style.setProperty('min-width', `${(maxColumn + 2) * 202}px`);
      calendarRef.current?.getApi?.()?.updateSize?.();
    }
  }, [calendarRef, eventsWithColumns]);

  const renderEventContent = useCallback(
    (arg) => (
      <EventCard
        event={arg.event}
        dateFormat={dateFormat}
        timeFormat={timeFormat}
        eventType='cl'
        noClSheets={noClSheets}
        hasEditAccess={hasEditAccess}
      />
    ),
    [dateFormat, timeFormat, noClSheets, hasEditAccess]
  );

  const eventClassNames = () => {
    return ['fc-event-width'];
  }

  const eventDidMount = (info) => {
    info.el.style.width = `200px`;

    const col = info.event.extendedProps?.column;
    info.el.style.left = `${col * TIME_VIEW_COLUMN_WIDTH}px`;

    const harness = info.el.parentElement;
    harness.style.right = "auto";
  };

  const viewDidMount = (...args) => {
    console.log(args)
  }

  return (
    <CalendarShell
      calendarRef={calendarRef}
      initialView="timeGridDay"
      initialDate={selectedDate}
      events={eventsWithColumns}
      renderEventContent={renderEventContent}
      minTime="00:00:00"
      maxTime="24:00:00"
      locale={timeSettings.is12Format ? '' : 'en-GB'}
      nowIndicator
      slotLabelFormat={slotLabelFormat}
      eventClassNames={eventClassNames}
      eventDidMount={eventDidMount}
      eventOverlap={true}
      slotEventOverlap={true}
      dayHeaders={true}
      dayHeaderContent={(arg) => (
        <CrewLeaderHeader crewLeaders={crewLeaders} /> 
      )}
      viewDidMount={viewDidMount}
    />
  );
};

CrewLeaderView.propTypes = {
  calendarRef: PropTypes.object,
  selectedDate: PropTypes.instanceOf(Date),
  dateFormat: PropTypes.string,
  timeFormat: PropTypes.string,
  noClSheets: PropTypes.arrayOf(PropTypes.string),
  hasEditAccess: PropTypes.bool,
};

export default CrewLeaderView;

